// Checkout form and handling

import { state } from '../state';
import { validateEmail, validateNigerianPhone, validateName } from '../utils/validation';
import { getTotalItemCount, getSelectedItems } from '../utils/calculations';
import { showToast } from '../ui/toast';
import { updateCheckoutButton } from './order-summary';

export function attachCheckoutFormListeners() {
    // Customer form inputs
    const nameInput = document.getElementById('customer-name') as HTMLInputElement;
    const emailInput = document.getElementById('customer-email') as HTMLInputElement;
    const phoneInput = document.getElementById('customer-phone') as HTMLInputElement;

    // Name input handler
    const handleNameInput = (e: Event) => {
        const value = (e.target as HTMLInputElement).value;
        state.customer.name = value;
        updateCheckoutButton();

        // Clear errors
        const errorLabel = document.getElementById('name-error-label');
        if (errorLabel) errorLabel.classList.add('hidden');
        if (nameInput) nameInput.classList.remove('input-error');
    };

    const validateNameInput = () => {
        if (state.customer.name && !validateName(state.customer.name)) {
            const errorMsg = 'Name must be between 3 and 100 characters';
            const errorEl = document.getElementById('name-error');
            const errorLabel = document.getElementById('name-error-label');
            if (errorEl) errorEl.textContent = errorMsg;
            if (errorLabel) errorLabel.classList.remove('hidden');
            if (nameInput) nameInput.classList.add('input-error');
        }
    };

    if (nameInput) {
        nameInput.addEventListener('input', handleNameInput);
        nameInput.addEventListener('blur', validateNameInput);
    }

    // Email input handler
    const handleEmailInput = (e: Event) => {
        const value = (e.target as HTMLInputElement).value;
        state.customer.email = value;
        updateCheckoutButton();

        // Clear errors
        const errorLabel = document.getElementById('email-error-label');
        if (errorLabel) errorLabel.classList.add('hidden');
        if (emailInput) emailInput.classList.remove('input-error');
    };

    const validateEmailInput = () => {
        if (state.customer.email && !validateEmail(state.customer.email)) {
            const errorMsg = 'Please provide a valid email address';
            const errorEl = document.getElementById('email-error');
            const errorLabel = document.getElementById('email-error-label');
            if (errorEl) errorEl.textContent = errorMsg;
            if (errorLabel) errorLabel.classList.remove('hidden');
            if (emailInput) emailInput.classList.add('input-error');
        }
    };

    if (emailInput) {
        emailInput.addEventListener('input', handleEmailInput);
        emailInput.addEventListener('blur', validateEmailInput);
    }

    // Phone input handler
    const handlePhoneInput = (e: Event) => {
        const value = (e.target as HTMLInputElement).value;
        state.customer.phone = value;
        updateCheckoutButton();

        // Clear errors
        const errorLabel = document.getElementById('phone-error-label');
        if (errorLabel) errorLabel.classList.add('hidden');
        if (phoneInput) phoneInput.classList.remove('input-error');
    };

    const validatePhoneInput = () => {
        if (state.customer.phone && !validateNigerianPhone(state.customer.phone)) {
            const errorMsg = 'Invalid phone number format (e.g., 08012345678)';
            const errorEl = document.getElementById('phone-error');
            const errorLabel = document.getElementById('phone-error-label');
            if (errorEl) errorEl.textContent = errorMsg;
            if (errorLabel) errorLabel.classList.remove('hidden');
            if (phoneInput) phoneInput.classList.add('input-error');
        }
    };

    if (phoneInput) {
        phoneInput.addEventListener('input', handlePhoneInput);
        phoneInput.addEventListener('blur', validatePhoneInput);
    }

    // Checkout form submission
    const form = document.getElementById('checkout-form') as HTMLFormElement;

    if (form) {
        form.addEventListener('submit', handleCheckout);
    }
}

async function handleCheckout(e: Event) {
    e.preventDefault();

    // Check if cart has items
    if (getTotalItemCount() === 0) {
        showToast('Please add items to your cart before proceeding', 'error');
        return;
    }

    // Clear previous errors
    ['name', 'email', 'phone'].forEach(field => {
        const errorLabel = document.getElementById(`${field}-error-label`);
        const inputEl = document.getElementById(`customer-${field}`) as HTMLInputElement;
        if (errorLabel) errorLabel.classList.add('hidden');
        if (inputEl) inputEl.classList.remove('input-error');
    });

    // Check each field and show specific errors
    let firstErrorField: string | null = null;

    if (!state.customer.name || state.customer.name.trim() === '') {
        const errorEl = document.getElementById('name-error');
        const errorLabel = document.getElementById('name-error-label');
        const inputEl = document.getElementById('customer-name') as HTMLInputElement;
        if (errorEl) errorEl.textContent = 'Please enter your full name';
        if (errorLabel) errorLabel.classList.remove('hidden');
        if (inputEl) inputEl.classList.add('input-error');
        if (!firstErrorField) firstErrorField = 'customer-name';
    } else if (!validateName(state.customer.name)) {
        const errorEl = document.getElementById('name-error');
        const errorLabel = document.getElementById('name-error-label');
        const inputEl = document.getElementById('customer-name') as HTMLInputElement;
        if (errorEl) errorEl.textContent = 'Name must be between 3 and 100 characters';
        if (errorLabel) errorLabel.classList.remove('hidden');
        if (inputEl) inputEl.classList.add('input-error');
        if (!firstErrorField) firstErrorField = 'customer-name';
    }

    if (!state.customer.email || state.customer.email.trim() === '') {
        const errorEl = document.getElementById('email-error');
        const errorLabel = document.getElementById('email-error-label');
        const inputEl = document.getElementById('customer-email') as HTMLInputElement;
        if (errorEl) errorEl.textContent = 'Please enter your email address';
        if (errorLabel) errorLabel.classList.remove('hidden');
        if (inputEl) inputEl.classList.add('input-error');
        if (!firstErrorField) firstErrorField = 'customer-email';
    } else if (!validateEmail(state.customer.email)) {
        const errorEl = document.getElementById('email-error');
        const errorLabel = document.getElementById('email-error-label');
        const inputEl = document.getElementById('customer-email') as HTMLInputElement;
        if (errorEl) errorEl.textContent = 'Please provide a valid email address';
        if (errorLabel) errorLabel.classList.remove('hidden');
        if (inputEl) inputEl.classList.add('input-error');
        if (!firstErrorField) firstErrorField = 'customer-email';
    }

    if (!state.customer.phone || state.customer.phone.trim() === '') {
        const errorEl = document.getElementById('phone-error');
        const errorLabel = document.getElementById('phone-error-label');
        const inputEl = document.getElementById('customer-phone') as HTMLInputElement;
        if (errorEl) errorEl.textContent = 'Please enter your phone number';
        if (errorLabel) errorLabel.classList.remove('hidden');
        if (inputEl) inputEl.classList.add('input-error');
        if (!firstErrorField) firstErrorField = 'customer-phone';
    } else if (!validateNigerianPhone(state.customer.phone)) {
        const errorEl = document.getElementById('phone-error');
        const errorLabel = document.getElementById('phone-error-label');
        const inputEl = document.getElementById('customer-phone') as HTMLInputElement;
        if (errorEl) errorEl.textContent = 'Invalid phone number format (e.g., 08012345678)';
        if (errorLabel) errorLabel.classList.remove('hidden');
        if (inputEl) inputEl.classList.add('input-error');
        if (!firstErrorField) firstErrorField = 'customer-phone';
    }

    // If there are errors, scroll to first error field and show toast
    if (firstErrorField) {
        const firstErrorInput = document.getElementById(firstErrorField);
        if (firstErrorInput) {
            firstErrorInput.focus();
            firstErrorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        showToast('Please fill in all required fields correctly', 'error');
        return;
    }

    const btn = document.getElementById('checkout-btn') as HTMLButtonElement;

    if (btn) {
        btn.disabled = true;
        btn.innerHTML = `
            <span class="loading loading-spinner loading-sm"></span>
            Processing...
        `;
    }

    try {
        // Prepare order data
        const items = getSelectedItems().map(product => ({
            product_id: product.id,
            quantity: state.selections.get(product.id) || 0
        }));

        const orderData = {
            customer_name: state.customer.name,
            customer_email: state.customer.email,
            customer_phone: state.customer.phone,
            items
        };

        // Create order
        const response = await fetch('/api/orders', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(orderData)
        });

        const data = await response.json();

        if (data.success && data.data) {
            // Redirect to Paystack
            window.location.href = data.data.payment.authorization_url;
        } else if (data.error?.code === 'ORDERS_CLOSED') {
            // Orders are closed - show special message
            showToast(data.error.message, 'error');
            if (btn) {
                btn.disabled = true;
                btn.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Orders Closed
                `;
            }
        } else {
            throw new Error(data.error?.message || 'Failed to create order');
        }
    } catch (error) {
        console.error('Error creating order:', error);
        showToast('Failed to process order. Please try again.', 'error');

        if (btn) {
            btn.disabled = false;
            btn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Proceed to Payment
            `;
        }
    }
}
